"use client";

import { Badge, Button, cn, Loader } from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import { format } from "date-fns";
import {
  ArrowLeftRight,
  CalendarDays,
  Clock,
  LogIn,
  LogOut,
  ScanBarcodeIcon,
} from "lucide-react";
import { useEffect } from "react";
import type {
  ShopShiftAssignmentRow,
  ShopShiftSwapRequestRow,
} from "@/modules/merchant/presentation/api/client";
import {
  assignmentStatusBadgeVariant,
  assignmentStatusLabel,
  assignmentStatusRowAccent,
  type ShiftWeekStats,
} from "@/modules/merchant/presentation/lib/shift-assignment-status";
import {
  formatShiftDuration,
  formatShiftTimeShort,
} from "@/modules/merchant/presentation/lib/shift-time-format";
import { ShiftSwapRequestStatusBadge } from "@/modules/merchant/presentation/ui/ShiftSwapRequestStatusBadge";
import { formatDateLocal } from "@/shared/lib/date-time";

function SummaryCards({ stats }: { stats: ShiftWeekStats }) {
  const cards = [
    {
      label: "ກະທັງໝົດ",
      value: stats.total,
      className: "border-border bg-muted/25",
    },
    {
      label: "ກຳລັງເປີດ",
      value: stats.open,
      className:
        "border-emerald-500/30 bg-emerald-500/10 text-emerald-950 dark:text-emerald-100",
    },
    {
      label: "ປິດແລ້ວ",
      value: stats.closed,
      className: "border-border bg-card",
    },
    {
      label: "ລໍຖ້າເຂົ້າ",
      value: stats.scheduled,
      className:
        "border-amber-500/30 bg-amber-500/10 text-amber-950 dark:text-amber-100",
    },
  ] as const;

  return (
    <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
      {cards.map((card) => (
        <div
          key={card.label}
          className={cn(
            "rounded-xl border px-3 py-3 sm:px-4 sm:py-3.5",
            card.className,
          )}
        >
          <p className="font-medium text-[10px] text-muted-foreground uppercase tracking-wide">
            {card.label}
          </p>
          <p className="mt-1 font-bold font-heading text-2xl tabular-nums tracking-tight">
            {card.value}
          </p>
        </div>
      ))}
    </div>
  );
}

function ActiveShiftBanner({
  row,
  canClock,
  clockBusy,
  onClockOut,
}: {
  row: ShopShiftAssignmentRow;
  canClock: boolean;
  clockBusy: boolean;
  onClockOut: () => void;
}) {
  return (
    <section className="mb-4 overflow-hidden rounded-xl border border-emerald-500/40 bg-emerald-500/10 shadow-sm">
      <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div>
          <p className="font-medium text-emerald-900 text-xs uppercase tracking-wide dark:text-emerald-200">
            ກຳລັງເປີດກະ
          </p>
          <p className="mt-1 font-semibold text-foreground text-base">
            {row.branchName} · {formatDateLocal(row.workDate)}
          </p>
          <p className="mt-0.5 text-muted-foreground text-sm tabular-nums">
            ເຂົ້າ {formatShiftTimeShort(row.clockInAt!)}
          </p>
        </div>
        {canClock ? (
          <Button
            type="button"
            className="h-10 shrink-0 gap-2"
            disabled={clockBusy}
            onClick={onClockOut}
          >
            <LogOut className="size-4" aria-hidden />
            ອອກງານ
          </Button>
        ) : null}
      </div>
    </section>
  );
}

function MyShiftRow({
  row,
  canClock,
  clockBusy,
  onClockIn,
  onClockOut,
}: {
  row: ShopShiftAssignmentRow;
  canClock: boolean;
  clockBusy: boolean;
  onClockIn: () => void;
  onClockOut: () => void;
}) {
  const hasClockIn = Boolean(row.clockInAt);
  const hasClockOut = Boolean(row.clockOutAt);

  return (
    <li
      className={cn(
        "border-l-[3px] px-4 py-3.5 sm:px-5",
        assignmentStatusRowAccent(row),
      )}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant={assignmentStatusBadgeVariant(row)}
              className="text-[10px]"
            >
              {assignmentStatusLabel(row)}
            </Badge>
            <span className="text-muted-foreground text-xs">{row.branchName}</span>
          </div>
          <p className="mt-1 font-medium text-foreground text-sm tabular-nums">
            {hasClockIn ? (
              <>
                {formatShiftTimeShort(row.clockInAt!)}
                <span className="mx-1.5 text-muted-foreground">→</span>
                {hasClockOut
                  ? formatShiftTimeShort(row.clockOutAt!)
                  : "—"}
              </>
            ) : (
              <span className="text-muted-foreground">
                ກຳນົດ {formatShiftTimeShort(row.scheduledStartAt)} –{" "}
                {formatShiftTimeShort(row.scheduledEndAt)}
              </span>
            )}
          </p>
          {hasClockIn && hasClockOut ? (
            <p className="mt-0.5 text-muted-foreground text-xs tabular-nums">
              {formatShiftDuration(row.clockInAt!, row.clockOutAt!)}
            </p>
          ) : null}
        </div>

        {canClock ? (
          <div className="flex shrink-0 gap-2">
            {!hasClockIn ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-10 gap-1.5"
                disabled={clockBusy}
                onClick={onClockIn}
              >
                <LogIn className="size-4" aria-hidden />
                ເຂົ້າງານ
              </Button>
            ) : null}
            {hasClockIn && !hasClockOut ? (
              <Button
                type="button"
                size="sm"
                className="h-10 gap-1.5"
                disabled={clockBusy}
                onClick={onClockOut}
              >
                <LogOut className="size-4" aria-hidden />
                ອອກງານ
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </li>
  );
}

function SwapRequestCard({
  row,
  canCancel,
  cancelBusy,
  onCancel,
}: {
  row: ShopShiftSwapRequestRow;
  canCancel: boolean;
  cancelBusy: boolean;
  onCancel: () => void;
}) {
  return (
    <li className="rounded-xl border border-border bg-muted/15 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <ShiftSwapRequestStatusBadge status={row.status} />
            <span className="font-medium text-foreground text-sm tabular-nums">
              {formatDateLocal(row.workDate)}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-muted-foreground text-sm">
            <span className="truncate">{row.sourceStaffUserEmail}</span>
            <ArrowLeftRight className="size-4 shrink-0" aria-hidden />
            <span className="truncate">{row.targetStaffUserEmail}</span>
          </div>
        </div>
        {canCancel ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={cancelBusy}
            onClick={onCancel}
          >
            ຍົກເລີກ
          </Button>
        ) : null}
      </div>
    </li>
  );
}

export type ShopShiftMyPanelsProps = {
  shopId: string | undefined;
  loading: boolean;
  stats: ShiftWeekStats;
  activeRows: ShopShiftAssignmentRow[];
  weekDays: Date[];
  byDate: Map<string, ShopShiftAssignmentRow[]>;
  canClock: boolean;
  clockBusy: boolean;
  onClockIn: (id: string) => void;
  onClockOut: (id: string) => void;
  swapRows: ShopShiftSwapRequestRow[];
  myStaffId: string | undefined;
  canSwap: boolean;
  canCreateSwap: boolean;
  swapCancelBusy: boolean;
  onCancelSwap: (id: string) => void;
  onOpenSwapDialog: () => void;
  highlightDayKey: string | null;
  scrollToDayKey: string | null;
  onScrolledToDay: () => void;
};

export function ShopShiftMyPanels({
  shopId,
  loading,
  stats,
  activeRows,
  weekDays,
  byDate,
  canClock,
  clockBusy,
  onClockIn,
  onClockOut,
  swapRows,
  myStaffId,
  canSwap,
  canCreateSwap,
  swapCancelBusy,
  onCancelSwap,
  onOpenSwapDialog,
  highlightDayKey,
  scrollToDayKey,
  onScrolledToDay,
}: ShopShiftMyPanelsProps) {
  useEffect(() => {
    if (!scrollToDayKey) return;
    const el = document.getElementById(`my-shift-day-${scrollToDayKey}`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
    onScrolledToDay();
  }, [scrollToDayKey, onScrolledToDay]);

  const openShift = activeRows.find(
    (r) => r.clockInAt && !r.clockOutAt,
  );

  const visibleDays = weekDays.filter((day) => {
    const key = format(day, "yyyy-MM-dd");
    return (byDate.get(key)?.length ?? 0) > 0;
  });

  if (loading) {
    return (
      <div className="flex justify-center rounded-xl border border-border bg-card py-20 shadow-sm">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <SummaryCards stats={stats} />

      {openShift ? (
        <ActiveShiftBanner
          row={openShift}
          canClock={canClock}
          clockBusy={clockBusy}
          onClockOut={() => onClockOut(openShift.id)}
        />
      ) : null}

      {activeRows.length === 0 ? (
        <section className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <div className="mb-4 flex size-14 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
              <CalendarDays className="size-7" aria-hidden />
            </div>
            <p className="font-heading font-semibold text-foreground text-lg">
              ບໍ່ມີກະໃນຊ່ວງນີ້
            </p>
            <p className="mt-1 max-w-sm text-muted-foreground text-sm">
              ເປີດກະຈາກໜ້າ POS ເພື່ອເລີ່ມຂາຍ
            </p>
            {shopId ? (
              <Link
                to="/app/shops/$shopId/pos"
                params={{ shopId }}
                className={cn(
                  "mt-5 inline-flex h-10 items-center gap-2 rounded-lg border px-4 font-medium text-sm transition-colors",
                  "border-emerald-500/35 bg-emerald-500/10 text-emerald-950 hover:bg-emerald-500/15 dark:text-emerald-100",
                )}
              >
                <ScanBarcodeIcon className="size-4" aria-hidden />
                ໄປໜ້າຂາຍ
              </Link>
            ) : null}
          </div>
        </section>
      ) : (
        <section className="mb-4 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <div className="flex items-center justify-between border-border border-b bg-muted/25 px-4 py-3 sm:px-5">
            <h2 className="font-heading font-semibold text-foreground text-base">
              ຕາຕະລາງກະ
            </h2>
            <span className="inline-flex items-center gap-1 text-muted-foreground text-xs">
              <Clock className="size-3.5" aria-hidden />
              {activeRows.length} ກະ
            </span>
          </div>
          <div className="divide-y divide-border">
            {visibleDays.map((day) => {
              const key = format(day, "yyyy-MM-dd");
              const rows = byDate.get(key) ?? [];
              const todayKey = format(new Date(), "yyyy-MM-dd");
              const today = key === todayKey;

              return (
                <div
                  key={key}
                  id={`my-shift-day-${key}`}
                  className={cn(
                    "scroll-mt-4",
                    highlightDayKey === key && "bg-primary/5",
                  )}
                >
                  <div
                    className={cn(
                      "flex items-center justify-between px-4 py-2.5 sm:px-5",
                      today
                        ? "bg-emerald-500/10"
                        : "bg-muted/15",
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-foreground text-sm tabular-nums">
                        {formatDateLocal(key)}
                      </h3>
                      {today ? (
                        <Badge variant="secondary" className="text-[10px]">
                          ມື້ນີ້
                        </Badge>
                      ) : null}
                    </div>
                    <span className="text-muted-foreground text-xs tabular-nums">
                      {rows.length} ກະ
                    </span>
                  </div>
                  <ul>
                    {rows.map((row) => (
                      <MyShiftRow
                        key={row.id}
                        row={row}
                        canClock={canClock}
                        clockBusy={clockBusy}
                        onClockIn={() => onClockIn(row.id)}
                        onClockOut={() => onClockOut(row.id)}
                      />
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 border-border border-b bg-muted/25 px-4 py-3 sm:px-5">
          <h2 className="flex items-center gap-2 font-heading font-semibold text-foreground text-base">
            <ArrowLeftRight className="size-4 text-amber-700 dark:text-amber-400" />
            ຄຳຂໍແລກກະ
          </h2>
          {canSwap && canCreateSwap ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-9"
              onClick={onOpenSwapDialog}
            >
              ຂໍແລກກະ
            </Button>
          ) : null}
        </div>
        {swapRows.length === 0 ? (
          <p className="px-4 py-10 text-center text-muted-foreground text-sm sm:px-5">
            ບໍ່ມີຄຳຂໍແລກກະ
          </p>
        ) : (
          <ul className="space-y-3 p-4 sm:p-5">
            {swapRows.map((row) => (
              <SwapRequestCard
                key={row.id}
                row={row}
                canCancel={
                  row.status === "pending" &&
                  myStaffId != null &&
                  row.requesterShopStaffId === myStaffId
                }
                cancelBusy={swapCancelBusy}
                onCancel={() => onCancelSwap(row.id)}
              />
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
